<template>
  <!-- IMPORTANT: avoid rendering while loading content -->
  <!-- This prevents children components from calling setup() without prefilled data -->
  <div class="pt-5 pb-1 main position-fixed bottom-50 end-0 start-0">
    <LoadingOverlay :loading="loading" />
  </div>
  <div class="main" v-if="!loading">
    <UserDataForm
      v-if="currentStep === 'USER_DATA'"
      :customTitle="t('PAYJOY_CARD_VIEWS.COMPLETE_USER_FORM_TITLE')"
      :preFilledData="userFormData"
      @onContinueClick="handleUseDataForm"
    />
    <ShippingAddressForm
      v-if="currentStep === 'ADDRESS'"
      @onContinueClick="handleAddressForm"
      @onCancelClick="handleBackClick"
      :customTitle="t('PAYJOY_CARD_VIEWS.COMPLETE_ADDRESS_FORM_TITLE')"
      :customSubmitCta="t('PAYJOY_CARD_VIEWS.COMPLETE_ADDRESS_SUBMIT_CTA')"
      :preFilledCityValue="preFilledCity"
      :preFilledRegionValue="preFilledRegion"
      :deviceTag="deviceTag"
      :preFilledData="userFormData"
    />
  </div>
  <FullScreenModal
    :isOpen="isErrorModalOpen"
    @onClose="closeModal"
    class="pt-5 pb-1"
  >
    <VerificationResult
      :icon="useAsset('img/credit-line/error_warning_icon.svg')"
      :title="
        t('PAYJOY_CARD_VIEWS.RESULT_SHIPPING_FORM.ERROR_SOMETHING_GOES_WRONG')
      "
      :show-device-tag="true"
      :description="t('PAYJOY_CARD_VIEWS.RESULT_SHIPPING_FORM.ERROR_TRY_AGAIN')"
      :btnText="t('PAYJOY_CARD_VIEWS.RESULT_SHIPPING_FORM.TRY_AGAIN')"
      :error="true"
      @dismissFunc="closeModal"
    >
    </VerificationResult>
  </FullScreenModal>
</template>

<script lang="ts">
import { defineComponent, getCurrentInstance, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import useAsset from "@/apps/credit-line/helpers/useAsset";

import ShippingAddressForm from "@/apps/credit-line/components/CreditCard/ShippingAddress/ShippingAddressForm.vue";
import UserDataForm from "@/apps/credit-line/components/CreditCard/UserDataForm.vue";
import FullScreenModal from "@/apps/credit-line/components/FullScreenModal.vue";
import VerificationResult from "@/apps/credit-line/components/Result.vue";
import LoadingOverlay from "@/shared-components/ui/core-app/LoadingOverlay.vue";
import { Service } from "@/apps/credit-line/services/network.service";

import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import { recorder, RECORDING_APPS } from "@/shared-services/recording.service";
import { isAndroid } from "@/apps/credit-line/helpers/platformChecker.helper";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import {
  ICreditCardAddressFormData,
  ICreditCardUserFormData,
} from "@/apps/credit-line/types/forms";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
// import Button from "@/apps/credit-line/components/Button.vue";
import { AxiosError } from "axios";
import { getRandomNumberBetweenRange } from "@/apps/credit-line/helpers/misc.helper";
import { useCreditLineSummary } from "@/apps/credit-line/hooks/useCreditLineSummary";
import { ICreditCardUserOverrideParams } from "../../types/requests";
import { PayJoyCardStatus } from "@/apps/credit-line/models/PayJoyCardStatus";
import { KycUserFlow } from "../../enums/kycEnums";

declare const Android: AndroidInterface;

export default defineComponent({
  name: "CreditCardShippingAddressView",
  computed: {
    KycUserFlow() {
      return KycUserFlow;
    },
  },
  components: {
    UserDataForm,
    ShippingAddressForm,
    LoadingOverlay,
    FullScreenModal,
    VerificationResult,
  },
  setup() {
    const { t } = useI18n();
    const CreditLineService = new Service();
    const router = useRouter();
    const deviceTag = ref<string>(
      AuthenticationHelper.getDeviceTag() as string
    );
    const userFormData = ref<ICreditCardUserFormData | null>(null);
    const addressFormData = ref<ICreditCardAddressFormData | null>(null);
    const isErrorModalOpen = ref<boolean>(false);
    const currentStep = ref<"USER_DATA" | "USER_DATA_MISSING" | "ADDRESS">(
      "USER_DATA"
    );
    const route = useRoute();
    const cardType = (route.query?.type as string) || "physical";
    const skipUserForm = (route.query?.skipUserForm as string) || null;
    const loading = ref<boolean>(true);
    const validationFailed = ref<boolean>(false);
    const preFilledCity = ref<string>("");
    const preFilledRegion = ref<string>("");
    const requiresIdentityResponse = ref();
    const { fetchSummary } = useCreditLineSummary();

    return {
      loading,
      isErrorModalOpen,
      preFilledCity,
      preFilledRegion,
      currentStep,
      router,
      CreditLineService,
      userFormData,
      addressFormData,
      deviceTag,
      cardType,
      skipUserForm,
      t,
      useAsset,
      route,
      validationFailed,
      requiresIdentityResponse,
      fetchSummary,
    };
  },
  async mounted() {
    const app = getCurrentInstance()?.appContext.app;
    recorder(app)?.startRecording(RECORDING_APPS.CREDIT_LINE);
    recorder(app)?.updateRecordingInfo(
      getRandomNumberBetweenRange(1000000, 1),
      {
        deviceTag: Android.getDeviceTag(),
      }
    );
    this.loading = true;

    try {
      const summary = await this.getInitialData();
      if (summary) {
        this.userFormData = {
          birthday: summary.user?.birthDate || "",
          email: "",
          gender: summary.user?.gender || "",
          firstName: summary.user?.firstName || "",
          lastName: summary.user?.lastName || "",
          identificationValue: summary.user?.idNumber || "",
          phoneNumber: "",
          taxIdentificationValue: "",
          source: "fetch",
        };
      }
      if (this.skipUserForm) {
        this.currentStep = "ADDRESS";
      }
    } catch (error) {
      // Log the error and continue. The response data is not essential for this view's forms.
      const axiosError = error as AxiosError;
      ErrorHandler.handleNetworkErrorAndLog(axiosError);
    }

    if (isAndroid()) {
      Android.finishedLoading();
    }
    this.loading = false;
  },
  methods: {
    handleBackClick() {
      if (this.currentStep === "ADDRESS") {
        this.currentStep = "USER_DATA";
        return;
      }
      this.router.push({ name: "CreditCardDetailsView" });
    },
    handleUseDataForm(data: ICreditCardUserFormData) {
      this.userFormData = data;
      this.currentStep = "ADDRESS";
    },
    async handleAddressForm(addressData: ICreditCardAddressFormData) {
      this.loading = true;
      const overrideParams: ICreditCardUserOverrideParams = {};
      overrideParams.firstName = this.userFormData?.firstName || undefined;
      overrideParams.birthday = this.userFormData?.birthday || undefined;
      overrideParams.lastName = this.userFormData?.lastName || undefined;
      overrideParams.gender = this.userFormData?.gender || undefined;
      overrideParams.phone = this.userFormData?.phoneNumber || undefined;
      overrideParams.streetName = addressData.street || undefined;
      overrideParams.streetNumber = addressData.streetNumber || undefined;
      overrideParams.city = addressData.city || undefined;
      overrideParams.region = addressData.region || undefined;
      overrideParams.zip = addressData.postalCode || undefined;
      try {
        await this.CreditLineService.createCardUser({
          deviceTag: this.deviceTag,
          overrideUserParams: overrideParams,
        });
        const summary = await this.getInitialData();
        // here is were the pomelo user creation links to instore activation card
        if (
          summary?.creditCardStatus ===
          PayJoyCardStatus.INSTORE_NEEDS_ACTIVATION
        ) {
          this.router.push({
            name: "CreditCardActivationView",
          });
        } else {
          this.router.push({
            name: "CreditCardTransitionView",
            query: { type: this.cardType },
          });
        }
      } catch (error) {
        const axiosError = error as AxiosError;
        ErrorHandler.handleNetworkErrorAndLog(axiosError);
        this.isErrorModalOpen = true;
      } finally {
        this.loading = false;
      }
    },
    closeModal() {
      this.isErrorModalOpen = false;
    },
    async getInitialData() {
      this.loading = true;
      try {
        return await this.fetchSummary();
      } catch (error) {
        return null;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/constants";
.header-banner {
  background-color: $pj-color-lighter-gray;

  .image {
    height: 80px;
  }
  .title {
    color: $pj-color-green;
  }
  .sub-title {
    color: $pj-color-cool-gray;
  }
}
.step-component {
  height: 100%;
  overflow-y: hidden;
  position: relative !important;
}

#wp-btn {
  border-radius: 10px;
  background: #46b871;
}
</style>
